import localforage from 'localforage';

// Create a separate store for console logs
const logsStore = localforage.createInstance({
  name: 'DebugLogs',
  storeName: 'consoleLogs'
});

class PersistentConsoleLogger {
  constructor() {
    this.maxLogs = 500; // Keep last 500 logs
    this.isInitialized = false;
    this.logBuffer = [];
    this.flushInterval = null;
  }

  async init() {
    if (this.isInitialized) return;

    // Load existing logs
    try {
      const existingLogs = await logsStore.getItem('logs') || [];
      this.logBuffer = existingLogs.slice(-this.maxLogs);
    } catch (error) {
      console.error('Failed to load existing logs:', error);
      this.logBuffer = [];
    }

    // Intercept console methods
    this.interceptConsole();

    // Start periodic flush to storage
    this.flushInterval = setInterval(() => {
      this.flushToStorage();
    }, 5000); // Flush every 5 seconds

    this.isInitialized = true;
  }

  shouldFilterMessage(message) {
    // Filter out common React warnings and other noise
    const filterPatterns = [
      // React warnings
      /Warning: React does not recognize the .* prop on a DOM element/,
      /Warning: Failed prop type:/,
      /Warning: componentWillReceiveProps has been renamed/,
      /Warning: componentWillMount has been renamed/,
      /Warning: componentWillUpdate has been renamed/,
      
      // ESLint warnings (handled by build system)
      /Line \d+:\d+:\s+.* no-unused-vars/,
      /overrideMethod @ hook\.js/,
      /printWarnings @ webpackHotDevClient\.js/,
      /handleWarnings @ webpackHotDevClient\.js/,
      
      // HMR and development noise
      /\[HMR\]/,
      /\[WDS\]/,
      /webpack/,
      /Hot Module Replacement/,
      
      // Network retry noise (expected offline behavior)
      /fetch.*failed/i,
      /net::ERR_INTERNET_DISCONNECTED/,
      /NetworkError/,
      
      // Performance marks (too verbose)
      /Performance mark/,
      /PerformanceObserver/,
      
      // LocalForage debug messages
      /localforage\.setItem/,
      /localforage\.getItem/,
      
      // Common library warnings
      /moment\.js/,
      /antd/,
      /styled-components/,
      
      // Self-referential logs to prevent infinite loops
      /persistentConsoleLogger/,
      /Failed to load existing logs/,
      /Debug logovi obrisani/,
    ];

    return filterPatterns.some(pattern => pattern.test(message));
  }

  interceptConsole() {
    const originalLog = console.log;
    const originalError = console.error;
    const originalWarn = console.warn;
    const originalInfo = console.info;

    const addLog = (type, args) => {
      try {
        const message = args.map(arg => {
          if (typeof arg === 'object') {
            try {
              return JSON.stringify(arg, null, 2);
            } catch (e) {
              return String(arg);
            }
          }
          return String(arg);
        }).join(' ');

        // Filter out unimportant messages
        if (this.shouldFilterMessage(message)) {
          return;
        }

        const logEntry = {
          type,
          message: message.substring(0, 500), // Limit message length
          timestamp: new Date().toISOString(),
          time: new Date().toLocaleTimeString(),
        };

        this.logBuffer.push(logEntry);

        // Keep only the last maxLogs entries
        if (this.logBuffer.length > this.maxLogs) {
          this.logBuffer = this.logBuffer.slice(-this.maxLogs);
        }
      } catch (error) {
        // Prevent infinite loops
      }
    };

    console.log = (...args) => {
      originalLog.apply(console, args);
      addLog('log', args);
    };

    console.error = (...args) => {
      originalError.apply(console, args);
      addLog('error', args);
    };

    console.warn = (...args) => {
      originalWarn.apply(console, args);
      addLog('warn', args);
    };

    console.info = (...args) => {
      originalInfo.apply(console, args);
      addLog('info', args);
    };

    // Store original methods for restoration
    this.originalMethods = {
      log: originalLog,
      error: originalError,
      warn: originalWarn,
      info: originalInfo
    };
  }

  async flushToStorage() {
    if (this.logBuffer.length === 0) return;

    try {
      await logsStore.setItem('logs', this.logBuffer);
    } catch (error) {
      // Silent fail - don't spam console
    }
  }

  async getLogs() {
    // Return current buffer + any logs from storage
    try {
      const storedLogs = await logsStore.getItem('logs') || [];
      return storedLogs;
    } catch (error) {
      return this.logBuffer;
    }
  }

  async clearLogs() {
    this.logBuffer = [];
    try {
      await logsStore.removeItem('logs');
    } catch (error) {
      console.error('Failed to clear logs:', error);
    }
  }

  async getLogsSummary() {
    const logs = await this.getLogs();
    const summary = {
      total: logs.length,
      errors: logs.filter(log => log.type === 'error').length,
      warnings: logs.filter(log => log.type === 'warn').length,
      recent: logs.slice(-10),
      oldestTimestamp: logs.length > 0 ? logs[0].timestamp : null,
      newestTimestamp: logs.length > 0 ? logs[logs.length - 1].timestamp : null,
    };

    return summary;
  }

  destroy() {
    if (this.flushInterval) {
      clearInterval(this.flushInterval);
      this.flushInterval = null;
    }

    // Restore original console methods
    if (this.originalMethods) {
      console.log = this.originalMethods.log;
      console.error = this.originalMethods.error;
      console.warn = this.originalMethods.warn;
      console.info = this.originalMethods.info;
    }

    // Final flush
    this.flushToStorage();
    this.isInitialized = false;
  }
}

// Create singleton instance
const persistentLogger = new PersistentConsoleLogger();

export default persistentLogger;